/**
 * Spanish FAQ translations
 * Source of truth match - English structure
 * Last Updated: January 5, 2026 | Version 3.0
 */
export const faq = {
  // Page Meta
  pageTitle: "Preguntas Frecuentes",
  heroSubtitle: "Todo lo que Necesita Saber Sobre el Observatorio de Futuros de la Juventud",
  heroDescription: "Respuestas completas sobre cómo funciona el Observatorio, cómo se desarrolló el Índice de Futuros de la Juventud y cómo puede usar o contribuir a esta plataforma.",
  lastUpdated: "Última Actualización: 5 de Enero de 2026",
  version: "Versión 3.0",
  availableLanguages: "Disponible en Inglés, Portugués, Español, Francés y Japonés",

  // Section Navigation
  sectionUnderstanding: "Comprendiendo",
  sectionMethodology: "Metodología",
  sectionData: "Datos y Fuentes",
  sectionUsing: "Usando el Observatorio",
  sectionFuture: "Desarrollo Futuro",
  sectionEvolve: "Plan 2026",
  sectionCommon: "Preguntas Comunes",
  sectionContact: "Contacto",

  // Section 1: Understanding the Observatory
  understandingTitle: "Comprendiendo el Observatorio",
  
  // Q1: What is the Youth Futures Observatory?
  q1Title: "¿Qué es el Observatorio de Futuros de la Juventud?",
  q1Answer: "El Observatorio de Futuros de la Juventud es una plataforma de inteligencia basada en evidencia que combina datos verificados de los registros institucionales de JCI e investigación global sobre juventud para mapear oportunidades para jóvenes en todo el mundo. Está diseñado para ayudar a las Organizaciones Nacionales y Locales de JCI a comprender el contexto más amplio en el que operan e identificar dónde sus esfuerzos de desarrollo de liderazgo tienen mayor impacto.",
  q1Detail: "En lugar de simplemente contar miembros o programas de JCI, el Observatorio hace una pregunta fundamental: ¿Cómo contribuye JCI a expandir las oportunidades para los jóvenes en todo el mundo?",
  q1Integrates: "Responde esto integrando:",
  q1IntegratesJCI: "Datos Institucionales de JCI — Miembros reales, programas, participación de miembros en 114 países",
  q1IntegratesGlobal: "Índices Globales de Oportunidad Juvenil — Datos comparativos sobre salud mental, desarrollo de fuerza laboral y emprendimiento en los mismos 114 países",
  q1IntegratesStrategic: "Documentación Estratégica — Metas de Visión 2040 y ambiciones de crecimiento de JCI hasta 2040",
  q1Result: "El resultado es una herramienta estratégica que muestra a JCI dónde expandirse, qué priorizar y cómo medir el éxito en el avance de la oportunidad global para jóvenes.",

  // Q2: Why "Youth Futures"?
  q2Title: "¿Por qué se llama Observatorio 'Futuros de la Juventud'?",
  q2Answer: "'Futuros de la Juventud' refleja un concepto importante de los estudios de futuros — el campo académico que examina cómo las decisiones y acciones moldean futuros posibles. El nombre codifica tres ideas clave:",
  q2Idea1Title: "El Plural de 'Futuros'",
  q2Idea1: "Los jóvenes no tienen un solo futuro predeterminado. Tienen múltiples futuros posibles dependiendo de las oportunidades disponibles, acceso a recursos, calidad de la educación, apoyo de salud mental y fortaleza del ecosistema emprendedor.",
  q2Idea2Title: "La Perspectiva de Estudios de Futuros",
  q2Idea2: "Los estudios de futuros nos enseñan que el futuro no está predeterminado — está moldeado por las decisiones que tomamos hoy. Al analizar las condiciones actuales, estamos mapeando los futuros probables que los jóvenes heredarán. Al identificar brechas, estamos identificando dónde JCI puede intervenir para crear mejores futuros.",
  q2Idea3Title: "Toma de Decisiones Basada en Datos",
  q2Idea3: "Comprender estos futuros requiere datos rigurosos sobre lo que está sucediendo ahora en cada región, para que los líderes puedan tomar decisiones informadas sobre dónde sus esfuerzos tendrán mayor impacto.",
  q2Summary: "El Observatorio da voz a la realidad de que la oportunidad juvenil no está distribuida uniformemente globalmente. Un joven en Europa enfrenta paisajes de oportunidad vastamente diferentes a un joven en África y Medio Oriente.",

  // Q3: Why was it created?
  q3Title: "¿Por qué los Consejos de Desarrollo de JCI crearon este Observatorio?",
  q3Answer: "El 4º Objetivo Estratégico del Plan Estratégico 2023-2027 de JCI es Innovación Basada en Datos. Este objetivo establece: 'Para ser una organización líder, necesitamos estar a la vanguardia de la tecnología e innovación.'",
  q3Questions: "El Observatorio fue creado para responder tres preguntas:",
  q3Question1: "¿Cómo probamos nuestro impacto? — JCI necesitaba una forma rigurosa y auditable de demostrar impacto a través de datos, no solo historias.",
  q3Question2: "¿Dónde debemos enfocar nuestros esfuerzos? — Con 4.641 Organizaciones Locales en 114 países, JCI necesita inteligencia estratégica sobre dónde los esfuerzos tienen mayor impacto.",
  q3Question3: "¿Cómo nos alineamos con prioridades globales? — Al mapear el trabajo de JCI contra índices globales de oportunidad juvenil, JCI puede demostrar alineación con los ODS de la ONU y posicionarse como socio estratégico.",
  q3Conclusion: "El Observatorio es Innovación Basada en Datos en la práctica — usando datos verificados y análisis sistemático para guiar decisiones estratégicas.",

  // Characteristics
  characteristics: {
    dataBased: "Basado en Datos Reales",
    dataBasedDesc: "Solo datos verificables y auditables",
    transparent: "Transparencia Total",
    transparentDesc: "Metodología completamente expuesta",
    collaborative: "Colaborativo",
    collaborativeDesc: "Construido con contribuciones de toda la comunidad JCI",
    accessible: "Accesible",
    accessibleDesc: "Gratuito y abierto para uso no comercial",
    scientific: "Científico",
    scientificDesc: "Alineado con estándares internacionales de investigación",
    inclusive: "Inclusivo",
    inclusiveDesc: "Refleja la diversidad global de JCI",
  },

  // Section 2: Observatory Methodology
  methodologyTitle: "Metodología del Observatorio",

  // Q4: How was the YFI developed?
  q4Title: "¿Cómo se desarrolló el Índice de Futuros de la Juventud?",
  q4Answer: "El Índice de Futuros de la Juventud (YFI) no es una nueva invención creada para JCI. Es una síntesis y adaptación de metodologías de investigación global establecidas que miden oportunidad juvenil.",
  q4Steps: "Así es cómo se desarrolló:",
  q4Step1Title: "Paso 1: Identificó Marcos Existentes",
  q4Step1: "Los equipos de investigación revisaron índices de oportunidad juvenil existentes de instituciones globales: Atlas de Salud Mental de la OMS, bases de datos de empleo de la OIT, estadísticas de educación de UNESCO, estudios del Monitor Global de Emprendimiento.",
  q4Step2Title: "Paso 2: Diseñó la Estructura del YFI",
  q4Step2: "Estas dimensiones se integraron en un único índice compuesto usando ponderación basada en investigación:",
  q4Weight1: "Salud Mental: 40% (acceso a servicios, tasas de prevalencia, mortalidad por suicidio)",
  q4Weight2: "Fuerza Laboral y Educación: 30% (desempleo juvenil, tasas NEET, desarrollo de habilidades)",
  q4Weight3: "Emprendimiento: 30% (ambiente de negocios, acceso a financiamiento, capacidad de innovación)",
  q4WeightRationale: "La ponderación refleja investigación que muestra que la oportunidad juvenil depende más fuertemente de la salud mental (que habilita todas las otras capacidades), seguida de la preparación laboral, luego la capacidad emprendedora.",
  q4Step3Title: "Paso 3: Aplicó a los 114 Países de JCI",
  q4Step3: "El marco del YFI se calculó para los 114 países donde JCI opera, creando un mapa global de oportunidad juvenil que se alinea directamente con la presencia geográfica de JCI.",
  q4Step4Title: "Paso 4: Validó los Resultados",
  q4Step4: "Los resultados se verificaron cruzadamente contra retroalimentación de miembros de JCI de encuestas de 2025 e investigación global publicada sobre desarrollo juvenil.",
  q4Result: "El Resultado: Un índice riguroso y auditable fundamentado en investigación establecida, no especulación o modelos.",

  // YFI Formula
  yfiFormulaTitle: "Fórmula del YFI",
  yfiFormula: "YFI = (0,40 × SM) + (0,30 × FL) + (0,30 × EMP)",
  yfiMH: "SM = Componente de Salud Mental (peso de 40%)",
  yfiWF: "FL = Componente de Fuerza Laboral y Educación (peso de 30%)",
  yfiENT: "EMP = Componente de Emprendimiento (peso de 30%)",

  // Q5: Dashcity Methodology
  q5Title: "¿Cómo funciona la Metodología de 5 Fases Dashcity?",
  q5Answer: "La Metodología de 5 Fases Dashcity proporciona la estructura estratégica de cómo se construyó el Observatorio y cómo evolucionará. Cada fase tiene un objetivo específico:",
  
  phase1: "IDENTIFICAR",
  phase1Status: "Completado",
  phase1Dates: "Sep-Nov 2025",
  phase1Objective: "Mapear el ecosistema completo de liderazgo juvenil",
  phase1Activities: "Analizó el Plan Estratégico de JCI 2023-2027; Diagnosticó el panorama institucional (114 países, 4.641 organizaciones locales, 148.122 miembros); Identificó la brecha: los datos de JCI existían en silos sin análisis unificado",
  phase1Deliverable: "Marco estratégico y alcance de investigación",

  phase2: "MEDIR",
  phase2Status: "Completado",
  phase2Dates: "Oct-Dic 2025",
  phase2Objective: "Establecer estándares rigurosos de datos y procesos de validación",
  phase2Activities: "Estableció 7 criterios de calidad no negociables; Verificó todas las fuentes de datos primarios (Informe de Impacto de JCI, encuestas, Visión 2040); Integró fuentes de datos globales (OMS, OIT, UNESCO, Banco Mundial, GEM); Implementó proceso de validación de 5 pasos",
  phase2Deliverable: "Arquitectura de datos y estándares de calidad",

  phase3: "PROMOVER",
  phase3Status: "En Progreso",
  phase3Dates: "Dic 2025-Ene 2026",
  phase3Objective: "Comunicar valor e involucrar a las partes interesadas",
  phase3Activities: "Desarrolló canales de participación de partes interesadas; Construyó pautas de contribución y estructuras de incentivos; Produjo recursos multilingües (EN, PT, ES, FR, JA)",
  phase3Deliverable: "Kit de comunicación e infraestructura de participación",

  phase4: "DIRIGIR",
  phase4Status: "En Progreso",
  phase4Dates: "Ene-Feb 2026",
  phase4Objective: "Establecer gobernanza y supervisión de calidad",
  phase4Activities: "Documentó procesos de validación (puerta de calidad de 7 pasos); Definió ciclos de actualización (anual, intermedio, continuo); Estableció bucles de retroalimentación (Comunidad → Equipos → Consejos → Junta)",
  phase4Deliverable: "Marco de gobernanza y procesos operativos",

  phase5: "ESCALAR",
  phase5Status: "Planificado",
  phase5Dates: "Ene 2026 - Dic 2027+",
  phase5Objective: "Expandir alcance, impacto e influencia global",
  phase5Activities: "Creando materiales de comunicación para diferentes audiencias; Escalar a 15+ Organizaciones Nacionales contribuyendo activamente; Crecer a 1.000+ usuarios registrados; Lanzar APIs públicas para investigadores académicos",
  phase5Deliverable: "Autoridad global en desarrollo de liderazgo juvenil",

  phaseWhyTitle: "Por qué Este Enfoque Funciona",
  phaseWhy: "Cada fase se basa en la anterior. IDENTIFICAR establece qué estamos construyendo. MEDIR asegura rigor. PROMOVER involucra a las partes interesadas. DIRIGIR asegura gobernanza. ESCALAR asegura crecimiento sostenible.",

  // Q6: Areas of Opportunity
  q6Title: "¿Cuáles son las 4 Áreas de Oportunidad de JCI?",
  areaIndividual: "Desarrollo Individual",
  areaIndividualDesc: "Los jóvenes desarrollan habilidades esenciales de liderazgo, confianza y crecimiento personal a través de capacitación, mentoría y experiencia práctica.",
  areaIndividualExamples: "Ejemplos: Oratoria, debate, cursos de liderazgo, certificación de habilidades",
  
  areaCommunity: "Impacto Comunitario",
  areaCommunityDesc: "Los jóvenes líderes identifican necesidades comunitarias y diseñan soluciones que benefician a la sociedad.",
  areaCommunityExamples: "Ejemplos: Iniciativas de salud, programas educativos, socorro en desastres, proyectos ambientales",
  
  areaInternational: "Cooperación Internacional",
  areaInternationalDesc: "Los jóvenes líderes construyen redes globales, entienden perspectivas diversas y colaboran a través de fronteras.",
  areaInternationalExamples: "Ejemplos: Asociaciones de hermanamiento JCI, premio TOYP, eventos internacionales, defensa de derechos humanos",
  
  areaBusiness: "Negocios y Emprendimiento",
  areaBusinessDesc: "Los jóvenes líderes lanzan negocios, impulsan innovación y construyen oportunidad económica.",
  areaBusinessExamples: "Ejemplos: Joven Emprendedor Creativo, JCI en Negocios, aceleradoras de startups",

  // Q7: Connection between Areas and Observatory
  q7Title: "¿Cuál es la conexión entre las 4 Áreas y el Observatorio?",
  q7Answer: "El Observatorio mide el contexto externo en el que operan estas 4 Áreas.",
  q7Detail: "El YFI rastrea: Salud Mental (afecta las 4 Áreas — mentes saludables aprenden mejor, construyen mejor), Fuerza Laboral y Educación (apoya áreas de Desarrollo Individual + Negocios), Emprendimiento (apoya área de Negocios).",
  q7Example: "Cuando el Observatorio muestra que África y Medio Oriente tiene puntuación YFI de 53 (vs. Europa en 75), está diciendo: 'Los jóvenes en estas regiones enfrentan brechas de salud mental, empleo y oportunidad de negocios. Aquí es donde las 4 Áreas de Oportunidad de JCI pueden tener máximo impacto.'",
  q7Summary: "Las 4 Áreas son cómo JCI desarrolla líderes. El Observatorio muestra dónde y por qué eso importa más.",

  // Q8: Data-Driven Innovation
  q8Title: "¿Qué es Innovación Basada en Datos (el 4º Objetivo Estratégico)?",
  q8Answer: "El 4º Objetivo Estratégico aborda preguntas críticas: ¿Cómo sabemos si nuestro desarrollo de liderazgo está funcionando realmente? ¿Cómo mejoramos? ¿Dónde debemos enfocarnos?",
  q8Means: "Innovación Basada en Datos significa:",
  q8Means1: "Medir lo que importa — Usar datos rigurosos sobre oportunidad juvenil, membresía de JCI e impacto",
  q8Means2: "Analizar para obtener insights — Encontrar patrones (ej: 'el acceso a salud mental varía 22 puntos entre regiones')",
  q8Means3: "Tomar decisiones informadas — Asignar recursos basándose en evidencia",
  q8Means4: "Mejora continua — Recopilar retroalimentación, ajustar estrategia, medir resultados",
  q8Conclusion: "El Observatorio transforma a JCI de una organización que siente que hace la diferencia a una que puede probar y medir su impacto usando datos verificados y auditables.",

  // Section 3: Data and Sources
  dataSourcesTitle: "Datos y Fuentes",

  // Q9: What data sources?
  q9Title: "¿Qué fuentes de datos usa el Observatorio?",
  primaryDataTitle: "Datos Primarios de JCI",
  primaryData1: "Informe de Impacto de JCI 2025 — 147.670 miembros, 4.641 organizaciones locales, 1.000+ proyectos anuales, 21M+ beneficiarios",
  primaryData2: "Visión 2040 de JCI — Hoja de ruta estratégica, metas de crecimiento, plan de innovación tecnológica",
  primaryData3: "Encuesta de Experiencia de Miembros 2025 — 3.767 encuestados en las 4 regiones",
  
  secondaryDataTitle: "Datos Globales Secundarios",
  secondaryData1: "Atlas de Salud Mental de la OMS 2024 — Cobertura de servicios de salud mental, estimaciones de prevalencia",
  secondaryData2: "Base de Datos de Mortalidad de la OMS — Tasas de mortalidad por suicidio por país",
  secondaryData3: "Estado Mental del Mundo Sapien Labs 2024 — Investigación de bienestar subjetivo",
  secondaryData4: "Tendencias Globales de Empleo de la OIT 2024-25 — Desempleo juvenil, estadísticas NEET",
  secondaryData5: "Instituto de Estadísticas de la UNESCO — Inscripciones educativas, tasas de alfabetización",
  secondaryData6: "Banco Mundial — Índice de Capital Humano, Facilidad para Hacer Negocios",
  secondaryData7: "Monitor Global de Emprendimiento GEM 2024-25 — Actividad emprendedora",
  secondaryData8: "Panel de Desarrollo Digital de la UIT — Infraestructura y acceso digital",
  
  dataTransparency: "Todas las fuentes son públicas y verificadas. El Observatorio contiene cero datos propietarios, confidenciales o restringidos.",

  // Q10: Data quality
  q10Title: "¿Cómo se garantiza la calidad de los datos?",
  q10Answer: "Implementamos 7 criterios de calidad no negociables:",
  qualityCriteria1: "Verificable — Identificación clara de la fuente (ej: 'Atlas de Salud Mental de la OMS 2024, página 12')",
  qualityCriteria2: "Auditable — Metodología completa documentada",
  qualityCriteria3: "Rastreable — Origen identificado, sin anonimato",
  qualityCriteria4: "Preciso — Validado antes de la presentación",
  qualityCriteria5: "Actual — Fecha clara y relevancia temporal",
  qualityCriteria6: "Contextualizado — Limitaciones claramente explicadas",
  qualityCriteria7: "Citado — Crédito completo a las fuentes",

  validationProcessTitle: "Proceso de Validación de 5 Pasos",
  validationStep1: "Envío (1-2 días)",
  validationStep1Desc: "Recepción de datos y verificación preliminar de completitud",
  validationStep2: "Validación (5-7 días)",
  validationStep2Desc: "Verificación técnica de metodología y cálculos",
  validationStep3: "Auditoría (5-7 días)",
  validationStep3Desc: "Auditoría independiente de precisión de datos",
  validationStep4: "Revisión por Pares (5-7 días)",
  validationStep4Desc: "Revisión por expertos en la materia",
  validationStep5: "Publicación (2-3 días)",
  validationStep5Desc: "Certificación final y documentación de transparencia",

  // Q11: Zero synthetic data
  q11Title: "¿Qué significa 'cero datos sintéticos'?",
  q11Answer: "Cero datos sintéticos significa: Cada número que ve rastrea hasta una fuente de datos real. Ningún número es inventado, estimado o generado por modelos.",
  
  notIncludedTitle: "NO incluimos",
  notIncluded1: "Proyecciones presentadas como hechos actuales",
  notIncluded2: "Estadísticas generadas por IA",
  notIncluded3: "Valores interpolados llenando brechas de datos",
  notIncluded4: "Estimaciones basadas en modelos (a menos que estén explícitamente etiquetadas)",
  notIncluded5: "Distribuciones asumidas",
  
  includedTitle: "Incluimos",
  included1: "Conteos reales (ej: '147.670 miembros')",
  included2: "Estadísticas basadas en encuestas (ej: '3.767 encuestados')",
  included3: "Índices publicados (ej: 'puntuación YFI: 75,28')",
  included4: "Escenarios explícitamente marcados (ej: 'Visión 2040: 500.000 miembros' — claramente etiquetado como meta estratégica)",
  
  zeroSyntheticWhy: "Por qué esto importa: Puede confiar en estos números. No son generados por algoritmos ni estimados por consultores. Son hechos verificados de fuentes auditables.",

  // Section 4: Using the Observatory
  usingTitle: "Usando el Observatorio",

  // Q12: Update cycles
  q12Title: "¿Con qué frecuencia se actualizan los datos?",
  annualUpdatesTitle: "Actualizaciones Anuales (Completas)",
  annualUpdates1: "Noviembre-Diciembre: Recolección de datos de todas las fuentes",
  annualUpdates2: "Enero-Febrero: Validación y análisis comparativo",
  annualUpdates3: "Marzo: Publicación de nueva edición",
  annualUpdates4: "Abril-Mayo: Retroalimentación y participación de partes interesadas",
  
  intermediateUpdatesTitle: "Actualizaciones Intermedias (Según Necesidad)",
  intermediateUpdates1: "Nuevos hallazgos de investigación significativos",
  intermediateUpdates2: "Cambios organizacionales importantes o innovaciones de programa",
  intermediateUpdates3: "Desarrollos importantes de políticas",
  intermediateUpdates4: "Correcciones de errores o aclaraciones",
  
  continuousUpdatesTitle: "Actualizaciones Continuas (En Curso)",
  continuousUpdates1: "Actualizaciones de enlaces y correcciones de referencias",
  continuousUpdates2: "Mejoras técnicas de la plataforma",
  continuousUpdates3: "Mejoras en la interfaz de usuario",
  continuousUpdates4: "Mejoras de accesibilidad",
  
  updateGoal: "El objetivo es mantener el Observatorio actual y preciso sin abrumar a los usuarios con microactualizaciones constantes.",

  // Q13: Using the data
  q13Title: "¿Puedo usar estos datos para mi investigación u organización?",
  q13Answer: "Sí, absolutamente. El Observatorio está diseñado para:",
  
  useNationalTitle: "Organizaciones Nacionales de JCI",
  useNational1: "Comparar su progreso contra datos globales",
  useNational2: "Identificar dónde sus esfuerzos regionales de liderazgo tienen mayor impacto",
  useNational3: "Tomar decisiones estratégicas basadas en datos sobre dónde expandirse",
  
  useLocalTitle: "Organizaciones Locales de JCI",
  useLocal1: "Entender el panorama de oportunidad juvenil en su ciudad/región",
  useLocal2: "Conectar sus proyectos locales con desafíos globales de oportunidad",
  useLocal3: "Rastrear si su trabajo está abordando brechas prioritarias",
  
  useAcademicTitle: "Investigadores Académicos",
  useAcademic1: "Acceso a datos verificados y metodología publicada",
  useAcademic2: "APIs para acceso programático (planificado para 2026)",
  useAcademic3: "Permiso para citar y desarrollar sobre hallazgos del Observatorio",
  
  usePolicyTitle: "Formuladores de Políticas y Gobierno",
  usePolicy1: "Evidencia sobre oportunidad juvenil en su país",
  usePolicy2: "Comparación con regiones vecinas",
  usePolicy3: "Inteligencia estratégica para políticas de educación, empleo, salud mental",
  
  useCorporateTitle: "Socios Corporativos",
  useCorporate1: "Entender necesidades de desarrollo juvenil en sus mercados",
  useCorporate2: "Identificar dónde las asociaciones con JCI podrían tener mayor impacto",
  useCorporate3: "Acceder a datos verificados para informes de responsabilidad corporativa",
  
  publicAPIs: "APIs Públicas — Lanzamiento en el 2º trimestre de 2026 para investigadores y desarrolladores",

  // Q14: Reproducibility
  q14Title: "¿Pueden los investigadores reproducir la metodología?",
  q14Answer: "Sí, completamente. Todos los componentes están documentados y públicamente disponibles:",
  q14Formula: "Fórmula del YFI (Publicada): YFI = (0,40 × SM) + (0,30 × FL) + (0,30 × EMP)",
  q14Sources: "Datos Fuente (Todos Públicos): Atlas de Salud Mental de la OMS, Base ILOSTAT de la OIT, estadísticas UNESCO, indicadores Banco Mundial, Monitor GEM, Informe de Impacto JCI 2025",
  q14Methodology: "Documentación Completa de la Metodología — Publicada íntegramente en la página de Metodología",
  q14Commitment: "Cualquier investigador con acceso a estas mismas fuentes puede replicar el análisis, verificar nuestros cálculos y probar nuestras conclusiones. Ese es el compromiso con la transparencia.",

  // Section 5: Future Development
  futureTitle: "Desarrollo Futuro",

  // Q15: Future roadmap
  q15Title: "¿Cuál es el futuro del Observatorio?",
  q15Answer: "El Observatorio está evolucionando a través de un plan de expansión por fases:",
  
  roadmap2026Title: "2026 (BETA — Expansión)",
  roadmap2026Items: "Escalar a 15+ Organizaciones Nacionales como contribuyentes activos; Crecer comunidad de usuarios a 200+ miembros; Lanzar APIs públicas para investigadores académicos; Desarrollar panel de análisis avanzado; Iniciar documentación de influencia en políticas",
  roadmap2026Target: "Metas: 200+ usuarios, 10+ artículos de medios anuales citando el Observatorio",
  
  roadmap2027Title: "2027+ (ESCALAR — Autoridad Global)",
  roadmap2027Items: "Posicionar como referencia global para desarrollo de liderazgo juvenil; Integración con plataformas de ONU (ECOSOC), WEF, UNESCO; Influencia visible en políticas en 10+ países; Comunidad global de 1.000+ usuarios comprometidos; Publicación anual reconocida en principales medios",
  roadmap2027Target: "Metas: 500+ artículos de medios anuales, influencia en políticas en 10+ países",
  
  innovationsTitle: "Innovaciones Estratégicas Planificadas",
  innovation2026: "2026: Directorio de Mentores, Páginas de Asociaciones, Contador de Abogacía en Tiempo Real",
  innovation2027: "2027+: Newsletter Mensual de Investigación, Serie de Webinars con Expertos, Herramientas Interactivas de Evaluación de Habilidades, Calculadora de Correspondencia de Subvenciones",

  // Q16: Contributing
  q16Title: "¿Cómo puedo contribuir al Observatorio?",
  
  contributeNationalTitle: "Las Organizaciones Nacionales pueden contribuir",
  contributeNational: "Análisis e insights regionales; Estudios de caso de programas exitosos; Datos de eventos y programas nacionales; Retroalimentación sobre cómo los hallazgos se aplican en su contexto",
  
  contributeLocalTitle: "Las Organizaciones Locales pueden contribuir",
  contributeLocal: "Resultados de proyectos y métricas de impacto; Historias de desarrollo de miembros; Datos sobre alcance comunitario y beneficiarios; Lecciones aprendidas de iniciativas locales",
  
  contributeMemberTitle: "Los Miembros Individuales pueden contribuir",
  contributeMember: "Retroalimentación sobre habilidades aprendidas a través de programas de JCI; Historias de cómo JCI cambió su trayectoria de liderazgo; Datos sobre resultados de empleo; Sugerencias de mejoras",
  
  contributeHowTitle: "Cómo contribuir",
  contributeHow1: "Visite la página de Contribución",
  contributeHow2: "Revise las pautas de contribución",
  contributeHow3: "Complete el formulario de contribución con documentación requerida",
  contributeHow4: "Reciba retroalimentación en 5-7 días",
  contributeHow5: "Sea reconocido como contribuidor cuando se publique",
  
  contributeIncentivesTitle: "Incentivos para contribuidores",
  contributeIncentive1: "Cita formal en todas las publicaciones del Observatorio",
  contributeIncentive2: "Certificado digital de colaboración",
  contributeIncentive3: "Reconocimiento en redes sociales",
  contributeIncentive4: "Válido para CV académico",
  contributeIncentive5: "Acceso a insights del Observatorio antes del lanzamiento público",

  // Q17: Vision 2040 alignment
  q17Title: "¿Cómo se alinea esto con la Visión 2040 de JCI?",
  q17Goal: "Meta de Visión 2040: Empoderar a 500.000 jóvenes líderes para 2040",
  q17Contribution: "Contribución del Observatorio: Inteligencia estratégica para lograr esta meta",
  
  vision2040PillarTitle: "Pilar de Visión 2040",
  vision2040RoleTitle: "Rol del Observatorio",
  
  vision2040Pillar1: "Excelencia en Desarrollo de Liderazgo",
  vision2040Role1: "Mapea brechas de habilidades (comunicación #1, pensamiento crítico #2); identifica prioridades de capacitación",
  
  vision2040Pillar2: "Tecnología e Innovación Digital",
  vision2040Role2: "Rastrea adopción digital (jci.cc: 13.351 usuarios, 260K seguidores sociales); guía hoja de ruta tecnológica",
  
  vision2040Pillar3: "Equidad e Inclusión",
  vision2040Role3: "Revela desigualdad regional de oportunidad (brecha de 22 puntos en YFI); identifica dónde el impacto es más necesario",
  
  vision2040Pillar4: "Sostenibilidad y Clima",
  vision2040Role4: "Proporciona contexto de oportunidad juvenil para alineación de políticas ambientales",
  
  vision2040QuestionsTitle: "Preguntas Estratégicas que el Observatorio Responde",
  vision2040Q1: "¿Dónde expandirse? — África y Medio Oriente (YFI más bajo = mayor oportunidad de impacto)",
  vision2040Q2: "¿Qué priorizar? — Salud mental (40% del peso del YFI = mayor diferenciador)",
  vision2040Q3: "¿Cómo escalar? — Tecnología (meta de 80%+ de adopción para 2040)",
  vision2040Q4: "¿Medidas de éxito? — Mejorar YFI global en 4-7 puntos; cerrar brecha de África y Medio Oriente en 50%",
  
  vision2040Summary: "El Observatorio transforma la Visión 2040 de una aspiración en una estrategia basada en evidencia.",

  // Section 6: 2026 Plan of Action (EVOLVE)
  evolveTitle: "Plan de Acción 2026: EVOLVE",

  // Q21: EVOLVE connection
  q21Title: "¿Cómo se relaciona el Observatorio con el Plan de Acción 2026?",
  q21Answer: "El Plan de Acción 2026 establece tres principios fundamentales: LEAD | CONNECT | EVOLVE. El Observatorio es una manifestación directa del principio EVOLVE.",
  q21Quote: "'Necesitamos LIDERAR con el enfoque que nuestra causa merece, CONECTAR con el cambio que queremos crear, y EVOLUCIONAR con el mundo en rápido movimiento a nuestro alrededor.'",
  
  evolveHow: "Cómo el Observatorio Habilita EVOLVE:",
  
  evolve1Title: "Evolucionando a Través de la Toma de Decisiones Basada en Datos",
  evolve1: "El Observatorio proporciona evidencia mostrando qué habilidades son más necesarias globalmente, dónde el desarrollo de liderazgo tiene mayor impacto, y cómo medir el éxito a través de indicadores de oportunidad juvenil.",
  evolve1Summary: "Sin datos, no podemos evolucionar estratégicamente — solo podemos reaccionar. El Observatorio permite evolución proactiva basada en evidencia.",
  
  evolve2Title: "Apoyando los 4 Pilares de Acción",
  actionPillar1: "Sostenibilidad Primero",
  actionPillar1Desc: "Rastrea salud mental (40% del peso del YFI) y oportunidad juvenil a largo plazo, asegurando que el trabajo cree futuros sostenibles",
  actionPillar2: "Construyendo Liderazgo Global",
  actionPillar2Desc: "Mapea dónde el desarrollo de liderazgo es más necesario; valida que los programas de JCI desarrollan habilidades que los jóvenes priorizan",
  actionPillar3: "Negocios Alrededor del Mundo",
  actionPillar3Desc: "Proporciona datos del ecosistema de emprendimiento (30% del peso del YFI); identifica dónde los programas de Negocios de JCI tienen mayor impacto",
  actionPillar4: "Conectando Vidas y Culturas",
  actionPillar4Desc: "Muestra diferencias regionales en oportunidad juvenil (brecha de 22 puntos en YFI), demostrando por qué la conexión intercultural importa",
  
  evolve3Title: "Midiendo KPIs que Importan",
  evolve3: "El Observatorio hace que los KPIs sean significativos conectándolos con datos externos de oportunidad juvenil. Por ejemplo, si vemos 30% de crecimiento en membresía en África y Medio Oriente, el Observatorio muestra que esto importa porque esa región tiene el YFI más bajo (53) y la mayor brecha de oportunidad.",
  
  evolve4Title: "'De Vuelta a lo Básico' se Encuentra con la Innovación Estratégica",
  evolve4: "El Observatorio complementa la Fase 1 'De Vuelta a lo Básico' del Plan 2026 proporcionando contexto estratégico para programas fundamentales, ofreciendo datos de benchmarking para certificación de capacitadores, y apoyando Mentor-Mentee 2.0 con evidencia.",
  
  evolve5Title: "Eventos Globales con Propósito",
  evolve5: "El Observatorio proporciona datos que hacen que los eventos globales (Cumbre de Liderazgo Juvenil, Semana de Sostenibilidad, Foros de Liderazgo Femenino) sean basados en evidencia en lugar de ceremoniales.",
  
  evolveKeyConnection: "Conexión Clave: EVOLVE significa adaptarse al mundo en rápido movimiento. El Observatorio es cómo JCI evoluciona — de liderazgo basado en intuición a liderazgo basado en datos, de 'creemos que hacemos la diferencia' a 'podemos probar nuestro impacto.'",
  
  evolveSummary: "El Plan de Acción 2026 establece la dirección (LEAD | CONNECT | EVOLVE). El Observatorio proporciona la inteligencia que hace la evolución estratégica, medible y auditable.",

  // Section 7: Common Questions
  commonTitle: "Preguntas Comunes",

  // Q22: Privacy
  q22Title: "¿Cómo aborda el Observatorio la privacidad y confidencialidad?",
  
  privacyMemberTitle: "Privacidad del Miembro",
  privacyMember1: "No se publican datos individuales de miembros",
  privacyMember2: "Todos los datos de encuestas se agregan y anonimizan",
  privacyMember3: "Los encuestados no pueden ser identificados en los resultados",
  privacyMember4: "La información personal está protegida según la política de privacidad",
  
  privacyOrgTitle: "Datos Organizacionales",
  privacyOrg: "Solo se publican datos agregados (ej: '147.670 miembros')",
  
  privacySecurityTitle: "Seguridad de Datos",
  privacySecurity1: "Transmisión y almacenamiento encriptados",
  privacySecurity2: "Auditorías de seguridad regulares",
  privacySecurity3: "Acceso limitado a datos brutos (solo Equipo de Operaciones)",
  privacySecurity4: "Cumplimiento con GDPR y leyes regionales de privacidad",

  // Q23: Disagreement
  q23Title: "¿Qué pasa si no estoy de acuerdo con los hallazgos del Observatorio?",
  q23Answer: "Su retroalimentación es esencial. El desacuerdo nos ayuda a mejorar.",
  q23Process: "Así es cómo:",
  q23Step1: "Envíe retroalimentación detallada a tmariano@jci.cc",
  q23Step2: "Especifique su preocupación (metodología, precisión de datos, interpretación)",
  q23Step3: "Su retroalimentación va al Consejo de Desarrollo relevante para revisión",
  q23Step4: "En 2 semanas, explicaremos nuestra metodología o reconoceremos su punto válido",
  q23Step5: "Si su retroalimentación revela un error, lo corregimos y lo citamos",
  q23Summary: "El Observatorio evoluciona a través del desacuerdo constructivo. No estamos tratando de tener razón — estamos tratando de ser precisos.",

  // Q24: Staying updated
  q24Title: "¿Cómo puedo mantenerme actualizado sobre los desarrollos del Observatorio?",
  
  newsletterTitle: "Newsletter (Lanzamiento 2º trimestre 2026)",
  newsletter1: "Destacados mensuales de nuevos hallazgos",
  newsletter2: "Destacados de contribuidores",
  newsletter3: "Próximas innovaciones",
  newsletter4: "Suscríbase en la página de Autenticación",
  
  webinarsTitle: "Webinars (Lanzamiento 3º trimestre 2026)",
  webinars1: "Explicaciones de expertos sobre dimensiones del Observatorio",
  webinars2: "Cómo usar datos del Observatorio para planificación estratégica",
  webinars3: "Preguntas y respuestas con Consejos de Desarrollo",
  webinars4: "Gratis, abierto a todos, multilingüe",
  
  directEmailTitle: "Email Directo",
  directEmail: "tmariano@jci.cc — Consultas generales, preguntas sobre datos o metodología específicos",

  // Contact Section
  contactTitle: "Contacto y Recursos",
  
  contactTechnicalTitle: "Para Preguntas Técnicas",
  contactTechnical: "tmariano@jci.cc",
  
  contactRegionalTitle: "Para Preguntas Regionales",
  contactAmericas: "América: adc@jci.cc",
  contactEurope: "Europa: edc@jci.cc",
  contactAfrica: "África y Medio Oriente: amdec@jci.cc",
  contactAsia: "Asia y Pacífico: apdc@jci.cc",
  
  keyResourcesTitle: "Recursos Clave",
  keyResource1: "Metodología Completa",
  keyResource2: "Panel de Datos",
  keyResource3: "Cómo Contribuir",
  
  accessibilityTitle: "Accesibilidad",
  accessibility1: "Disponible en Inglés, Portugués, Español, Francés y Japonés",
  accessibility2: "PDFs descargables de todo el contenido (próximamente)",
  accessibility3: "Compatible con lectores de pantalla",
  accessibility4: "Idiomas adicionales bajo solicitud",

  // CTA
  ctaTitle: "¿Listo para Explorar?",
  ctaDescription: "El Observatorio de Futuros de la Juventud está construido sobre evidencia. Nuestro compromiso es con la transparencia, el rigor y la mejora continua en apoyo de la misión de JCI de desarrollar líderes para un mundo cambiante.",
  ctaExplore: "Explorar el Observatorio",
  ctaContribute: "Contribuir al Observatorio",
  ctaContact: "Contáctenos",

  // Source
  sourceAttribution: "Fuente: Plan Estratégico JCI 2023-2027, Metodología DASHCity, Informe de Impacto JCI 2025",
};